import dayjs from 'dayjs';
import { useLocalization } from '@fluent/react';

import ContactPointCard from '#/components/cards/ContactPointCard';
import Timeline, { TimelineItem } from '#/components/display/Timeline';
import { FragmentType, getFragmentData } from '#/__generated__/graphql';
import {
  RouteLegDetailsFragmentDoc,
  TripDetailsPanelFragmentDoc,
} from '#/__generated__/graphql/graphql';
import { formatAddress, formatUnitCode } from '#/utils/formatter';

const TripDetailsPanel: React.FC<TripDetailsPanelProps> = ({ order: _order }) => {
  const { l10n } = useLocalization();
  const order = getFragmentData(TripDetailsPanelFragmentDoc, _order);
  const routeLeg = getFragmentData(RouteLegDetailsFragmentDoc, order.acceptedOffer.routeLegs[0]);

  return (
    <div>
      <Timeline className="pt-4">
        <TimelineItem
          secondaryText={
            <div className="relative -top-6">
              <div>
                <div className="mb-1 text-xs uppercase text-neutral-400">
                  {l10n.getString('pick-up')}
                </div>
                <time className="text-lg text-neutral-400">
                  {dayjs(order.acceptedOffer.pickupTime).format('lll')}
                </time>
              </div>
              <div className="relative flex my-5 gap-x-5 after:absolute after:border-b after:border-gray-300 after:left-0 after:w-full after:top-1/2 after:-z-50">
                <TimeStopTag
                  text={dayjs(order.acceptedOffer.pickupTime).from(
                    order.acceptedOffer.deliveryTime,
                    true,
                  )}
                />
                <TimeStopTag text={1 + ' ' + l10n.getString('stop')} />
              </div>
            </div>
          }
        >
          <div className="relative -top-6">
            <div className="flex divide-x">
              <div className="flex-auto basis-1/2">
                <div className="mb-2 text-xs uppercase text-neutral-400">
                  {l10n.getString('from')}
                </div>
                <div className="mb-5 font-bold text-black">
                  {formatAddress(routeLeg.startLocation.address)}
                </div>
              </div>
              <TripContactPersonCard
                label={l10n.getString('contact-person')}
                name={routeLeg.startContactPoint.name ?? ''}
                telephone={routeLeg.startContactPoint.telephone ?? ''}
              />
            </div>
          </div>
        </TimelineItem>
        <TimelineItem
          secondaryText={
            <div className="relative -top-6">
              <div className="mb-1 text-xs uppercase text-neutral-400">
                {l10n.getString('estimated-arrival')}
              </div>
              <time className="text-lg text-neutral-400">
                {dayjs(order.acceptedOffer.deliveryTime).format('lll')}
              </time>
            </div>
          }
        >
          <div className="relative flex divide-x -top-6">
            <div className="flex-auto basis-1/2">
              <div className="mb-2 text-xs uppercase text-neutral-400">{l10n.getString('to')}</div>
              <div className="mb-5 font-bold text-black">
                {formatAddress(routeLeg.endLocation.address)}
              </div>
            </div>
            <TripContactPersonCard
              label={l10n.getString('contact-person')}
              name={routeLeg.endContactPoint.name ?? ''}
              telephone={routeLeg.endContactPoint.telephone ?? ''}
            />
          </div>
        </TimelineItem>
      </Timeline>

      {order.orderedItems.length > 0 && (
        <div className="flex flex-col gap-2 ml-8">
          <div className="text-xs uppercase text-neutral-400">
            {l10n.getString('cargo-details')}
          </div>
          <div className="space-y-5">
            <div className="grid grid-cols-2">
              <div>
                <p className="text-sm font-semibold">{l10n.getString('preferred-vehicle')}</p>
                <p className="text-lg text-neutral-600">
                  {order.acceptedOffer.preferredVehicle?.name}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold">{l10n.getString('estimated-weight')}</p>
                <p className="text-lg text-neutral-600">
                  {order.acceptedOffer.cargoDetails.cargoWeight.value}{' '}
                  {formatUnitCode(order.acceptedOffer.cargoDetails?.cargoWeight?.unitCode ?? '')}
                </p>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold">{l10n.getString('dimensions')}</p>
              <div className="text-lg text-neutral-600">
                <span>
                  {order.acceptedOffer.cargoDetails.cargoDimensions.width.value}{' '}
                  {formatUnitCode(
                    order.acceptedOffer.cargoDetails.cargoDimensions.width.unitCode ?? '',
                  )}{' '}
                  ({l10n.getString('width')}) x{' '}
                </span>
                <span>
                  {order.acceptedOffer.cargoDetails.cargoDimensions.length.value}{' '}
                  {formatUnitCode(
                    order.acceptedOffer.cargoDetails.cargoDimensions.length.unitCode ?? '',
                  )}{' '}
                  ({l10n.getString('length')}) x{' '}
                </span>
                <span>
                  {order.acceptedOffer.cargoDetails.cargoDimensions.height.value}{' '}
                  {formatUnitCode(
                    order.acceptedOffer.cargoDetails.cargoDimensions.height.unitCode ?? '',
                  )}{' '}
                  ({l10n.getString('height')})
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold">{l10n.getString('description')}</p>
              <div className="text-lg text-neutral-600">{order.orderedItems[0].description}</div>
            </div>
          </div>
          <div className="pt-4 text-xs uppercase text-neutral-400">
            {l10n.getString('additional-note')}
          </div>
          <div className="text-lg text-neutral-600">{order.acceptedOffer.note}</div>
          {!!order.acceptedOffer.attachments && order.acceptedOffer.attachments.length > 0 && (
            <div className="flex flex-col space-y-3">
              <div className="pt-4 text-xs uppercase text-neutral-400">
                {l10n.getString('attachments')}
              </div>
              {order.acceptedOffer.attachments.map((file) => (
                <a
                  className="max-w-fit"
                  download={file.name}
                  href={file.url}
                  key={file.url}
                  target="_blank"
                >
                  <div className="px-3 py-2 border border-black rounded-full hover:bg-slate-200">
                    {file.name}
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export type TripDetailsPanelProps = {
  order: FragmentType<typeof TripDetailsPanelFragmentDoc>;
};

const TripContactPersonCard: React.FC<TripContactPersonCardProps> = ({
  label,
  name,
  telephone,
}) => {
  return (
    <div className="basis-1/2">
      <div className="max-w-xs ml-8">
        <div className="mb-2 text-xs uppercase text-neutral-400">{label}</div>
        <ContactPointCard name={name} telephone={telephone} />
      </div>
    </div>
  );
};
type TripContactPersonCardProps = {
  label: string;
  name: string;
  telephone: string;
};

const TimeStopTag: React.FC<{ text: string }> = ({ text }) => {
  return (
    <div className="p-2 text-xs uppercase bg-white border border-gray-300 text-neutral-400 rounded-3xl">
      {text}
    </div>
  );
};

export default TripDetailsPanel;
